<template>
  <AssetCard :view-mode="viewMode" :empty-title="project ? null : 'Aucun projet associé'" :empty-message="null"
    :show-edit-button="showEditButton" :show-delete-button="showDeleteButton" :created-at="project?.created_at ?? null"
    :memos-count="memosCount" :table-grid-class="'flex items-center'" :class="customClass" :is-owned="isOwned"
    :is-public="isPublic" :is-private="!isPublic" :show-ownership-badge="showOwnershipBadge" :user-id="userId"
    :can-edit="canEditProject" :can-delete="canDeleteProject" @click="handleClick" @edit="handleEdit"
    @delete="handleDelete">
    <!-- Mode Table -->
    <template #table-row="{ classes }">
      <!-- Titre/Acronyme et informations -->
      <div class="flex items-center gap-2 min-w-0 flex-[1.5]">
        <CircuitBoard :class="classes.icon" />
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span :class="[classes.title, 'truncate', shouldShowTooltip ? 'relative group' : '']"
              :title="shouldShowTooltip ? tooltipTitle : undefined">
              {{ displayTitle }}
            </span>
            <span v-if="project?.type" class="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs shrink-0">{{
              project.type }}</span>
          </div>
          <div v-if="project?.title && project?.acronym && !shouldShowTooltip && displayTitle !== project.title"
            class="text-xs text-gray-500 truncate">
            {{ project.title }}
          </div>
        </div>
      </div>

      <!-- Financeur -->
      <div class="hidden md:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ project?.fundedby || 'Non spécifié' }}</span>
      </div>

      <!-- Compteur mémos -->
      <div class="flex items-center gap-1 text-sm text-gray-600 min-w-0 px-3">
        <ClipboardIcon :class="classes.footerIcon" />
        <span>{{ memosCount }}</span>
      </div>

      <!-- Actions -->
      <TableRowActions :classes="classes" :show-edit-button="showEditButton" :show-delete-button="showDeleteButton"
        :can-edit="canEditProject" :can-delete="canDeleteProject" @edit="handleEdit" @delete="handleDelete" />
    </template>

    <!-- Mode Memos Table -->
    <template #memos-table-icon="{ classes }">
      <CircuitBoard :class="classes.icon" />
    </template>

    <template #memos-table-content="{ classes }">
      <template v-if="project">
        <div class="flex items-center gap-2">
          <h3 :class="[classes.title, shouldShowTooltip ? 'relative group' : '']"
            :title="shouldShowTooltip ? tooltipTitle : undefined">
            {{ displayTitle }}
          </h3>
          <span v-if="project.type" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs shrink-0">
            {{ project.type }}
          </span>
          <span v-if="project.aurehal_id"
            class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs flex items-center gap-1 shrink-0">
            <Hash class="h-3.5 w-3.5" />
            {{ project.aurehal_id }}
          </span>
        </div>
        <div class="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <span v-if="project.title && project.acronym && !shouldShowTooltip && displayTitle !== project.title"
            class="truncate">{{ project.title }}</span>
          <span v-if="project.fundedby" class="truncate flex items-center gap-1">
            <Building class="h-3.5 w-3.5" />
            {{ project.fundedby }}
          </span>
        </div>
      </template>
      <div v-else class="text-gray-500 text-sm flex items-center gap-2">
        <CircuitBoard class="h-3.5 w-3.5" />
        <span>Aucun projet associé</span>
      </div>
    </template>

    <!-- Mode Default -->
    <template #default="{ classes }">
      <div class="flex items-center justify-between mb-2">
        <h3 :class="[classes.title, shouldShowTooltip ? 'relative group' : '']"
          :title="shouldShowTooltip ? tooltipTitle : undefined">
          {{ displayTitle }}
        </h3>
      </div>

      <div v-if="project?.type || project?.aurehal_id" class="flex flex-wrap gap-2 mt-2">
        <span v-if="project.type" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
          {{ project.type }}
        </span>
        <span v-if="project.aurehal_id"
          class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs flex items-center gap-1">
          <Hash class="h-3 w-3" />
          {{ project.aurehal_id }}
        </span>
      </div>

      <p v-if="project?.description" class="text-sm text-gray-600 mt-2">
        {{ project.description }}
      </p>

      <div v-if="project?.title && project?.acronym" class="text-xs font-medium text-gray-500 mt-2">
        {{ project.title }}
      </div>


    </template>

    <!-- Mode Default Info -->
    <template #info="{ classes }">
      <div v-if="project?.fundedby" :class="classes.iconWithText">
        <Building :class="classes.icon" />
        <span>{{ project.fundedby }}</span>
      </div>
      <div v-if="project?.link" :class="classes.iconWithText">
        <BookOpen :class="classes.icon" />
        <span>{{ project.link }}</span>
      </div>
    </template>

  </AssetCard>
</template>

<script setup lang="ts">
import {
  BookOpen,
  Building,
  CircuitBoard,
  Clipboard as ClipboardIcon,
  Hash
} from 'lucide-vue-next';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import type { Project, ProjectWithMemos } from '../../api/types';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import AssetCard from '../UI/AssetCard.vue';
import TableRowActions from '../UI/TableRowActions.vue';

const props = defineProps<{
  project: Project | ProjectWithMemos | null | undefined;
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showTopIcon?: boolean;
  viewMode?: 'default' | 'table' | 'memostable';
  customClass?: string;
  // Nouvelles props pour les permissions et propriété
  isOwned?: boolean;
  isPublic?: boolean;
  showOwnershipBadge?: boolean;
  userId?: string;
}>();

const emit = defineEmits<{
  (e: 'add'): void;
  (e: 'edit', project: Project): void;
  (e: 'delete', uuid: string): void;
}>();

const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();

const hasMemos = computed(() => {
  if (!props.project) return false;
  return 'memos' in props.project && Array.isArray((props.project as ProjectWithMemos).memos);
});

// Computed pour les permissions
const canEditProject = computed((): boolean => {
  return props.project ? authStore.canEditAsset(props.project as any) : false;
});

const canDeleteProject = computed((): boolean => {
  return props.project ? authStore.canDeleteAsset(props.project as any) : false;
});

const memosCount = computed(() => {
  if (!props.project || !hasMemos.value) return 0;
  return (props.project as ProjectWithMemos).memos?.length || 0;
});



// Logique pour afficher titre complet ou acronyme avec tooltip
const displayTitle = computed(() => {
  if (!props.project) return 'Sans titre';

  const title = props.project.title;
  const acronym = props.project.acronym;

  // Si pas de titre, utiliser l'acronyme
  if (!title) return acronym || 'Sans titre';

  // Si pas d'acronyme, utiliser le titre
  if (!acronym) return title;

  // Si le titre est court (≤ 50 caractères), l'afficher
  if (title.length <= 50) return title;

  // Sinon, afficher l'acronyme
  return acronym;
});

const shouldShowTooltip = computed(() => {
  if (!props.project?.title || !props.project?.acronym) return false;
  return props.project.title.length > 50;
});

const tooltipTitle = computed(() => {
  return props.project?.title || '';
});



const handleEdit = (): void => {
  if (props.project) {
    emit('edit', props.project);
  }
};

const handleDelete = (): void => {
  if (props.project?.uuid) {
    emit('delete', props.project.uuid);
  }
};

const handleClick = (): void => {
  if (!props.project) {
    return;
  }

  const projectData = {
    uuid: props.project.uuid,
    acronym: props.project.acronym,
    title: props.project.title,
  };

  history.replaceState({ project: projectData }, '');

  const projectName = props.project.acronym || props.project.title || 'Projet';
  breadcrumbsStore.updateBreadcrumbs([
    { name: 'Mes projets', path: '/myprojects' },
    { name: projectName, path: `/project/${props.project.uuid}` }
  ]);

  router.push(`/project/${props.project.uuid}`);
};
</script>